import React, { useEffect, useState } from 'react';
import { Button, Icon, Modal, Loader, Dimmer, Checkbox } from 'semantic-ui-react';
import { CancellationFormik } from '../Formik/CancellationFormFormik';
import { CancelModalPolicyProps, RelatedPolicy } from '../../../types';
import withReactContent from 'sweetalert2-react-content';
import { post } from '../../../../../services/request';
import {
  postFileAndGetPreSignAxios,
  deleteFilesLinkAxios
} from '../../../../../services/file';
import {
  fileToBlob,
  sanitizeFilename,
  validateFileAccessibility
} from '../../../../../utils/fileUtils';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../../../services/notification';
import Swal from 'sweetalert2';
import {
  getRefundInfo,
  cancelAlfaSegurosProposal,
  cancelGeralProposal,
  cancelYelumProposal,
  cancelPortoSeguroProposal
} from '../../../../../services/fire-insurance-proposal';
import { getPolicies } from '../../../../../services/fire-insurance-policy';
import moment from 'moment-timezone';
import { translateProposalStatus } from '../../../../fire-insurance/utils/Pottencial';
import { InsurersCodeEnum } from '../../../../../enums/insurers-code.enum';

const CancelModalPolicy = ({
  closeModal,
  callback,
  policyData
}: CancelModalPolicyProps) => {
  const [flagSubmitFrom, setFlagSubmitFrom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFirePolicies, setSelectedFirePolicies] = useState<string[]>([]);
  const [termsAccepted, setTermsAccepted] = useState({
    noReactivation: false,
    newAnalysis: false
  });
  const [relatedPolicies, setRelatedPolicies] = useState<RelatedPolicy[]>([]);

  const handleFirePolicySelection = (selectedPolicies: string[]) => {
    setSelectedFirePolicies(selectedPolicies);
  };

  const MySwal = withReactContent(Swal);

  const cancelFirePolicy = async (
    policy: RelatedPolicy,
    notificationEmails: string,
    reason: string,
    observations: string
  ) => {
    try {
      const isYelum = policy.insurerCode === InsurersCodeEnum.Yelum;

      const payload = {
        policyId: policy.id,
        notificationEmails,
        reason,
        observations,
        ...(isYelum
          ? {}
          : {
              refundValue: policy.refundValue || 0,
              expectedPaymentDate: policy.expectedPaymentDate,
            }),
      };

      let response;

      switch (policy.insurerCode) {
        case InsurersCodeEnum.alfa:
          response = await cancelAlfaSegurosProposal(payload);
          break;
        case InsurersCodeEnum.Yelum:
          response = await cancelYelumProposal(payload);
          break;
        default:
          response = await cancelGeralProposal(payload);
          break;
      }

      successNotification(
        'Sucesso',
        `Seguro de incêndio ${policy.id} cancelado com sucesso.`
      );
      return true;
    } catch (error) {
      console.error('Erro ao cancelar seguro de incêndio:', error);
      dangerNotification(
        'Erro',
        `Não foi possível cancelar o seguro de incêndio ${policy.id}.`
      );
      return false;
    }
  };

  async function submitFiles(
    files: {
      file: File;
      name: string;
    }[]
  ) {
    const responses = [];
    for (const file of files) {
      let fileId: string | null = null;

      try {
        // Validar se o arquivo está acessível ANTES de obter o presigned URL
        await validateFileAccessibility(file.file);

        // const contentType = file.file.type || 'application/octet-stream';

        const response = await postFileAndGetPreSignAxios({
          filename: sanitizeFilename(file.file.name),
          contentType: file.file.type,
          referencedTableName: 'bond_insurance',
          referencedTableId: policyData.bondInsuranceId,
          name: file.name
        });

        fileId = response.id;

        const blob = await fileToBlob(file.file);

        const uploadResponse = await fetch(response.uploadURL, {
          method: 'PUT',
          body: blob
        });

        if (!uploadResponse.ok) {
          throw new Error(
            `Falha no upload: ${uploadResponse.status} ${uploadResponse.statusText}`
          );
        }

        responses.push({ id: response.id, contextType: file.name });
      } catch (uploadError) {
        // Se falhou após criar a entrada no banco, fazer rollback
        if (fileId) {
          try {
            console.log(`[Upload] Rollback: removendo entrada órfã ${fileId}`);
            await deleteFilesLinkAxios(fileId);
          } catch (deleteError) {
            console.error(
              `[Upload] Falha ao remover entrada órfã ${fileId}:`,
              deleteError
            );
          }
        }
        throw uploadError;
      }
    }
    return responses;
  }

  const handleTermsChange = (field: 'noReactivation' | 'newAnalysis') => {
    setTermsAccepted({ ...termsAccepted, [field]: !termsAccepted[field] });
  };

  async function handleSubmit(payload: any) {
    MySwal.fire({
      title: `Atenção`,
      text: 'Esta ação é irreversível e consequentemente não será possível realizar a abertura de sinistro para indenização. Deseja prosseguir? ',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, desejo prosseguir',
      cancelButtonText: 'Não'
    }).then(async result => {
      if (result.isConfirmed) {
        setLoading(true);

        try {
          await submitFiles(payload.files);
        } catch (error) {
          console.error('Erro ao enviar arquivos:', error);
          const errorMessage =
            (error as Error)?.message ||
            'Não foi possível enviar os arquivos. Verifique e tente novamente.';
          dangerNotification('Erro', errorMessage);
          setLoading(false);
          return;
        }
        try {
          const cancelPayload = {
            hiredDataId: payload.hiredDataId,
            userId: payload.userId,
            cancellationReason: payload.cancellationReason,
            observations: payload.observations,
            notificationEmails: payload.notificationEmails,
            files: payload.files
          };

          await post('/canceled-bond-insurances/policy', cancelPayload);

          if (selectedFirePolicies.length > 0) {
            const notificationEmails = payload.notificationEmails.join(',');

            const selectedPolicies = relatedPolicies.filter(policy =>
              selectedFirePolicies.includes(policy.id)
            );

            for (const policy of selectedPolicies) {
              await cancelFirePolicy(
                policy,
                notificationEmails,
                payload.cancellationReason,
                payload.observations || ''
              );
            }
          }

          successNotification(
            'Sucesso',
            'Solicitação de Cancelamento recebida com sucesso.'
          );

          callback();
          setLoading(false);
          setFlagSubmitFrom(false);
          closeModal();
        } catch (err) {
          console.error('Erro ao processar o cancelamento:', err);
          dangerNotification('Erro', 'Erro ao efetuar solicitação.', 4000);
          setLoading(false);
          setFlagSubmitFrom(false);
        }
      } else {
        closeModal();
      }
    });
  }

  useEffect(() => {
    getPolicies({
      document: policyData.document,
      zipCode: policyData.zipCode,
      estateId: policyData.estateId
    }).then(response => {
      const data = response.data;
      const policies = data.map((item: any) => {
        const analysis = item.fireInsuranceAnalysis;
        const fireInsurance = analysis.fireInsurance;

        return {
          id: item.id,
          status: translateProposalStatus(item.status),
          insurerName: analysis.insurer.name,
          insurerCode: analysis.insurer.code,
          estateName: fireInsurance.estate.name,
          createdAt: moment(item.createdAt).format('DD/MM/YYYY'),
          policyNumber: item.insurerPolicyId || 'N/A',
          address:
            fireInsurance.propertyAddresses.street +
            ', ' +
            fireInsurance.propertyAddresses.number,
          validity: {
            startDate: moment(item.policyPeriodStart).format('DD/MM/YYYY'),
            endDate: moment(item.policyPeriodEnd).format('DD/MM/YYYY')
          },
          vigency: `${moment(item.policyPeriodStart).format('DD/MM/YYYY')} a ${moment(
            item.policyPeriodEnd
          ).format('DD/MM/YYYY')}`,
          totalPremium: {
            value: Number(analysis.grossPremium),
            installments: analysis.installments,
            installmentValue: Number(analysis.value)
          },
          premium: Number(analysis.grossPremium).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }),
          refundValue: 0,
          expectedPaymentDate: undefined
        };
      });

      const alfaPolicies = policies.filter(
        (policy: RelatedPolicy) => policy.insurerCode === InsurersCodeEnum.alfa
      );

      if (alfaPolicies.length > 0) {
        Promise.all(
          alfaPolicies.map(async (policy: RelatedPolicy) => {
            try {
              const refundInfo = await getRefundInfo(policy.id);
              if (refundInfo) {
                policy.refundValue = refundInfo.refundValue
                  ? Number(refundInfo.refundValue)
                  : 0;
                policy.expectedPaymentDate = refundInfo.expectedPaymentDate
                  ? moment(refundInfo.expectedPaymentDate, 'YYYY-MM-DD').toDate()
                  : undefined;
              }
              return policy;
            } catch (error) {
              console.error(
                `Erro ao obter informações de reembolso para política ${policy.id}:`,
                error
              );
              return policy;
            }
          })
        ).then(() => {
          setRelatedPolicies([...policies]);
        });
      } else {
        setRelatedPolicies(policies);
      }
    });
  }, []);

  const isConfirmEnabled = termsAccepted.noReactivation && termsAccepted.newAnalysis;

  return (
    <Modal
      open={true}
      onClose={closeModal}
      centered={false}
      closeOnEscape={false}
      closeOnDimmerClick={false}
    >
      <Dimmer active={loading} inverted>
        <Loader size="medium" />
      </Dimmer>

      <Modal.Header>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Icon name="trash alternate" size="small" />
          <div style={{ marginLeft: '10px' }}>
            Cancelamento de Apólice
            {relatedPolicies.length > 0 && (
              <div style={{ fontSize: '0.8em', color: '#555' }}>
                Existem {relatedPolicies.length} seguros de incêndio relacionados
              </div>
            )}
          </div>
        </div>
      </Modal.Header>

      <Modal.Content>
        <CancellationFormik
          loading={loading}
          callbackSubmit={handleSubmit}
          flagSubmit={flagSubmitFrom}
          setFlagSubmit={setFlagSubmitFrom}
          hiredDataId={policyData.hiredDataId}
          closeModal={closeModal}
          relatedPolicies={relatedPolicies}
          onFirePoliciesSelected={handleFirePolicySelection}
        />

        <div style={{ marginTop: '20px' }}>
          <Checkbox
            label="Estou ciente que esta operação é irreversível."
            checked={termsAccepted.noReactivation}
            onChange={() => handleTermsChange('noReactivation')}
          />
        </div>
        <div style={{ marginTop: '10px' }}>
          <Checkbox
            label="Estou ciente que após cancelado não poderei prosseguir com essa apólice, sendo necessário fazer uma nova análise caso preciso for."
            checked={termsAccepted.newAnalysis}
            onChange={() => handleTermsChange('newAnalysis')}
          />
        </div>
      </Modal.Content>

      <Modal.Actions>
        <Button color="red" onClick={closeModal} disabled={loading}>
          Cancelar
        </Button>
        <Button
          color="green"
          disabled={!isConfirmEnabled || loading}
          loading={loading}
          onClick={() => setFlagSubmitFrom(true)}
        >
          Confirmar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
export default CancelModalPolicy;
